/**
 * Videos Page - List of generated timelapse videos
 * Shows video jobs with progress, preview, and download functionality
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, Typography, Button, Space, Empty, Progress, Tag, App, Skeleton, Grid } from 'antd';
import AppLayout from '@/components/AppLayout';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import {
  subscribeToVideoJobs,
  getVideoStatusLabel,
  getVideoStatusColor,
  formatVideoDuration,
  deleteVideoJob,
} from '@/services/videoService';
import type { VideoJob } from '@/types';

const { Title, Text } = Typography;

// Format date for display
function formatDate(timestamp: { toDate: () => Date } | Date | null): string {
  if (!timestamp) return '-';
  const date = 'toDate' in timestamp ? timestamp.toDate() : timestamp;
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format file size
function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Calculate time remaining until expiry
function getExpiryInfo(expiresAt: { toDate: () => Date } | Date | null): { text: string; isExpired: boolean } {
  if (!expiresAt) return { text: '-', isExpired: false };
  const date = 'toDate' in expiresAt ? expiresAt.toDate() : expiresAt;
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  if (diff <= 0) return { text: 'Expired', isExpired: true };

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return { text: `${hours}h ${minutes}m remaining`, isExpired: false };
  }
  return { text: `${minutes}m remaining`, isExpired: false };
}

// Video job card component
function VideoJobCard({
  job,
  onDelete,
}: {
  job: VideoJob;
  onDelete: (jobId: string) => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const screens = Grid.useBreakpoint();
  // `md` is the project's mobile breakpoint (matches the 767px cutoff in index.css).
  // Below it, stack preview/info and actions vertically instead of a fixed 3-column row.
  const isMobile = !screens.md;
  const expiryInfo = job.expiresAt ? getExpiryInfo(job.expiresAt) : null;
  const isInProgress = ['pending', 'downloading', 'encoding', 'uploading'].includes(job.status);
  const canDownload = job.status === 'completed' && job.downloadUrl && !expiryInfo?.isExpired;

  return (
    <Card
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: 16 } }}
    >
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {/* Video preview/icon */}
        <div
          style={{
            width: isMobile ? '100%' : 120,
            // Desktop keeps the compact 120x80 thumb. On mobile the preview
            // spans full width, so use an aspect ratio (between 4:3 and 16:9)
            // instead of a fixed height, which would look squashed.
            ...(isMobile ? { aspectRatio: '16 / 10' } : { height: 80 }),
            backgroundColor: '#f0f0f0',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {job.status === 'completed' && !expiryInfo?.isExpired ? (
            <video
              src={job.downloadUrl}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
              muted
              preload="metadata"
            />
          ) : (
            <MaterialIcon
              name={job.status === 'failed' ? 'error' : 'movie'}
              size={40}
              style={{ color: job.status === 'failed' ? '#ff4d4f' : '#bfbfbf' }}
            />
          )}
        </div>

        {/* Job info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: 16, wordBreak: 'break-word' }}>
              {job.fileName || 'Video'}
            </Text>
            <Tag color={getVideoStatusColor(job.status)}>
              {getVideoStatusLabel(job.status)}
            </Tag>
          </div>

          <Space size="large" wrap>
            <Text type="secondary">
              <MaterialIcon name="photo_library" size={21} style={{ marginRight: 4, position: 'relative', top: -2 }} />
              {job.photoCount} photos
            </Text>
            <Text type="secondary">
              <MaterialIcon name="aspect_ratio" size={21} style={{ marginRight: 4, position: 'relative', top: -2 }} />
              {job.resolution}
            </Text>
            <Text type="secondary">
              <MaterialIcon name="speed" size={21} style={{ marginRight: 4, position: 'relative', top: -2 }} />
              {job.fps} FPS
            </Text>
            {job.duration && (
              <Text type="secondary">
                <MaterialIcon name="timer" size={21} style={{ marginRight: 4, position: 'relative', top: -2 }} />
                {formatVideoDuration(job.duration)}
              </Text>
            )}
            {job.fileSize && (
              <Text type="secondary">
                <MaterialIcon name="hd" size={21} style={{ marginRight: 4, position: 'relative', top: -2 }} />
                {formatFileSize(job.fileSize)}
              </Text>
            )}
          </Space>

          {/* Progress bar for in-progress jobs */}
          {isInProgress && (
            <div style={{ marginTop: 12 }}>
              <Progress
                percent={job.progress || 0}
                status="active"
                strokeColor={{ from: '#108ee9', to: '#87d068' }}
              />
              {job.progressDetails && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {job.progressDetails}
                </Text>
              )}
            </div>
          )}

          {/* Error message */}
          {job.status === 'failed' && job.error && (
            <div style={{ marginTop: 8 }}>
              <Text type="danger" style={{ fontSize: 12 }}>
                <MaterialIcon name="error" size={14} style={{ marginRight: 4 }} />
                {job.error}
              </Text>
            </div>
          )}

          {/* Created date and expiry info */}
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Created: {formatDate(job.createdAt)}
              {job.status === 'completed' && expiryInfo && !expiryInfo.isExpired && (
                <span>
                  {' / '}
                  <Text type="warning" style={{ fontSize: 12 }}>
                    Download available for {expiryInfo.text.replace(' remaining', '')}
                  </Text>
                </span>
              )}
              {job.status === 'completed' && expiryInfo?.isExpired && (
                <span>
                  {' / '}
                  <Text type="danger" style={{ fontSize: 12 }}>
                    Download expired
                  </Text>
                </span>
              )}
            </Text>
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'row' : 'column',
            gap: 8,
            width: isMobile ? '100%' : 'auto',
            flexShrink: 0,
          }}
        >
          {canDownload && (
            <Button
              type="primary"
              icon={<MaterialIcon name="download" />}
              loading={downloading}
              disabled={downloading}
              block={isMobile}
              onClick={async () => {
                if (!job.downloadUrl || downloading) return;
                setDownloading(true);
                try {
                  // Fetch the video blob (works with CORS/cross-origin URLs)
                  const response = await fetch(job.downloadUrl);
                  const blob = await response.blob();

                  // Create local blob URL and trigger download
                  const blobUrl = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = job.fileName || "timelapse-video.mp4";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);

                  // Clean up blob URL
                  URL.revokeObjectURL(blobUrl);
                } catch (error) {
                  console.error("Download failed:", error);
                  // Fallback to opening in new tab
                  window.open(job.downloadUrl, "_blank");
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
          )}
          <Button
            icon={<MaterialIcon name="delete" />}
            onClick={() => onDelete(job.jobId)}
            danger
            block={isMobile}
          >
            Delete
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function VideosPage() {
  const { effectiveUid } = useEffectiveUserId();
  const { modal, message } = App.useApp();
  const [videoJobs, setVideoJobs] = useState<VideoJob[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to video jobs
  useEffect(() => {
    if (!effectiveUid) {
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToVideoJobs(effectiveUid, (jobs) => {
      setVideoJobs(jobs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [effectiveUid]);

  // Sort jobs: in-progress first, then by date
  const sortedJobs = useMemo(() => {
    return [...videoJobs].sort((a, b) => {
      const aInProgress = ['pending', 'downloading', 'encoding', 'uploading'].includes(a.status);
      const bInProgress = ['pending', 'downloading', 'encoding', 'uploading'].includes(b.status);

      if (aInProgress && !bInProgress) return -1;
      if (!aInProgress && bInProgress) return 1;

      // Sort by date descending
      const aDate = a.createdAt ? ('toDate' in a.createdAt ? a.createdAt.toDate() : a.createdAt) : new Date(0);
      const bDate = b.createdAt ? ('toDate' in b.createdAt ? b.createdAt.toDate() : b.createdAt) : new Date(0);
      return bDate.getTime() - aDate.getTime();
    });
  }, [videoJobs]);

  const handleDelete = (jobId: string) => {
    const job = videoJobs.find(j => j.jobId === jobId);
    const isInProgress = job && ['pending', 'downloading', 'encoding', 'uploading'].includes(job.status);

    modal.confirm({
      title: isInProgress ? 'Cancel and delete video job?' : 'Delete video job?',
      content: isInProgress
        ? 'This will cancel the video generation and remove it from the list.'
        : 'This will remove the video from the list. If the video file still exists, it will be deleted from storage.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteVideoJob(jobId);
          message.success('Video job deleted');
        } catch (error) {
          console.error('Error deleting video job:', error);
          message.error('Failed to delete video job');
        }
      },
    });
  };

  return (
    <AppLayout>
      <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
        <Title level={2}>
          Video renderings
        </Title>

        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Generated timelapse videos from your photo collections.
        </Text>

        {loading ? (
          // Single stable skeleton shaped like the real video cards — the list
          // reveals in one shot when the first jobs snapshot arrives
          <div>
            {[0, 1].map((i) => (
              <Card key={i} style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 120,
                      height: 80,
                      backgroundColor: '#f0f0f0',
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Skeleton active title={false} paragraph={{ rows: 3 }} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : sortedJobs.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No videos yet"
          >
            <Text type="secondary">
              Select photos in the gallery and click "Create Video" to generate a timelapse video.
            </Text>
          </Empty>
        ) : (
          <div>
            {sortedJobs.map((job) => (
              <VideoJobCard key={job.jobId} job={job} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
